import type { Candle, MarketData } from "../types";
import type { Indicator } from "./Indicator";
import { rollingSum, sma } from "./math";

// Felacoo tick-native indicators. They read per-candle tick counts recorded by the TickAggregator.
// On server-built candles (>= 1m from Deriv) tick counts are 0, so values are null there.

function hasTicks(c: Candle) { return c.volume > 0; }

function durationSec(c: Candle, d: MarketData): number {
  if (d.tf.kind === "sec" || d.tf.kind === "candle") return d.tf.seconds!;
  return Math.max(1, c.endTime - c.time + 1);
}

export const TickSpeed: Indicator = {
  id: "tickSpeed", name: "Tick Speed (ticks/s)", group: "tick", defaults: { smooth: 5 },
  calculate: (d, s, color) => {
    const raw = d.candles.map((c) => (hasTicks(c) ? c.volume / durationSec(c, d) : 0));
    const smoothed = sma(raw, s.smooth).map((v, i) => (hasTicks(d.candles[i]) ? v : null));
    return {
      panel: {
        id: "tickSpeed", title: `Tick Speed ×${s.smooth}`,
        histogram: { values: raw.map((v, i) => (hasTicks(d.candles[i]) ? v : null)), posColor: "rgba(245,166,35,0.35)" },
        lines: [{ values: smoothed, color }],
      },
    };
  },
};

export const TickDominance: Indicator = {
  id: "tickDominance", name: "Tick Dominance (% up-ticks)", group: "tick", defaults: { period: 20 },
  calculate: (d, s, color) => {
    const ups = rollingSum(d.candles.map((c) => c.upTicks), s.period);
    const downs = rollingSum(d.candles.map((c) => c.downTicks), s.period);
    const values = ups.map((u, i) => {
      const dn = downs[i];
      if (u == null || dn == null || u + dn === 0 || !hasTicks(d.candles[i])) return null;
      return (u / (u + dn)) * 100;
    });
    return { panel: { id: "tickDominance", title: `Tick Dominance ${s.period}`, lines: [{ values, color }], range: [0, 100], levels: [30, 50, 70] } };
  },
};

export const TickImbalance: Indicator = {
  id: "tickImbalance", name: "Tick Imbalance (up − down)", group: "tick", defaults: { period: 20 },
  calculate: (d, s, color) => {
    const net = rollingSum(d.candles.map((c) => c.upTicks - c.downTicks), s.period);
    const values = net.map((v, i) => (hasTicks(d.candles[i]) ? v : null));
    return { panel: { id: "tickImbalance", title: `Tick Imbalance ${s.period}`, histogram: { values }, lines: [{ values: sma(values.map((v) => v ?? 0), 5), color, width: 1 }], levels: [0] } };
  },
};

export const TICK_INDICATORS = [TickSpeed, TickDominance, TickImbalance];
